import { rampVolume } from '@signage-player/shared-logic';
import { tHtml } from './i18n';
import { rendererLogger } from './logger';
import { VideoSyncController } from './videoSync';
export class Player {
    constructor(mainContainerElements, showMainContainer, transformMediaPath, videoSoundEnabled = true, fadeDuration = 1500, videoSyncCorrection = true) {
        this.mainContainerElements = mainContainerElements;
        this.showMainContainer = showMainContainer;
        this.transformMediaPath = transformMediaPath;
        this.videoSoundEnabled = videoSoundEnabled;
        this.fadeDuration = fadeDuration;
        // Set by a play that reached the screen, cleared by the stop that took it off again.
        this.currentlyPlaying = null;
        this.preloaded = null;
        // Bumped per element (buffer × type) on every preload: tells a pending play whether its src survived.
        this.loadGen = new Map();
        // Bumped on every forced stop (event list regenerated): plays received before it are void, main re-queues survivors.
        // Not covered by the newest-due rule below: after a regeneration into a gap the old pending play is itself the newest due one.
        this.stopGen = 0;
        // Plays are numbered at receipt; main's newest due play names what belongs on screen, older ones firing after it are stale.
        this.playSeq = 0;
        this.newestDuePlay = { seq: 0, itemId: null };
        this.pendingPlays = new Map();
        this.deferredSyncs = new Map();
        this.activeTimers = new Set();
        // Volume ramps around ad spots. The fade decision lives HERE (and only
        // here): the double buffer knows both neighbors — `currentlyPlaying` is the
        // predecessor while a successor preloads, and `preloaded` is the successor
        // while the current item approaches its stop. Fades shape volume only; the
        // start_at seek at preload (the mid-track music resume) is never touched.
        this.activeRamps = new Map();
        this.pendingFadeInItemId = null;
        this.videoSync = videoSyncCorrection ? new VideoSyncController() : null;
    }
    getCurrentlyPlaying() {
        return this.currentlyPlaying;
    }
    getPreloaded() {
        return this.preloaded;
    }
    getVideoSyncStatus() {
        return this.videoSync?.getStatus() ?? null;
    }
    /** Runtime kill switch: restores rate 1 on a video already being corrected. */
    disableVideoSync() {
        this.videoSync?.reset();
        this.videoSync = null;
    }
    static initialize(mainContainer, showMainContainer, transformMediaPath, videoSoundEnabled = true, fadeDuration = 1500, videoSyncCorrection = true) {
        const playerInstance = new Player(mainContainer, showMainContainer, transformMediaPath, videoSoundEnabled, fadeDuration, videoSyncCorrection);
        playerInstance.setupPlayerListeners();
        return playerInstance;
    }
    setupPlayerListeners() {
        if (window.electronAPI && window.electronAPI.player) {
            window.electronAPI.player.onInitialize(() => {
                this.showMainContainer();
            });
            window.electronAPI.player.onPreloadCommand((item, odd, mediaPath) => {
                this.handlePreloadCommand(item, odd, mediaPath);
            });
            window.electronAPI.player.onPlayCommand((item, odd, when) => {
                this.handlePlayCommand(item, odd, when);
            });
            window.electronAPI.player.onStopCommand((item, _odd, when, force) => {
                this.handleStopCommand(item, when, force);
            });
            window.electronAPI.player.onIdleCommand((isSomethingPlanned, nextItemAt, isExiting) => {
                this.handleIdleCommand(isSomethingPlanned, nextItemAt, isExiting);
            });
            window.electronAPI.player.onSyncCommand((item, odd, when, seekTo) => {
                this.handleSyncCommand(item, odd, when, seekTo);
            });
        }
    }
    handlePreloadCommand(item, odd, mediaPath) {
        if (this.preloaded && this.preloaded.item.id === item.id) {
            return;
        }
        if (this.preloaded) {
            rendererLogger.debug('Destroying previously preloaded window for different playable', { previousId: this.preloaded.item.id, newId: item.id });
        }
        // Re-queue of the item on screen onto its own element: reassigning src would halt it, so leave it as it plays.
        if (this.currentlyPlaying?.item.id === item.id && this.currentlyPlaying.odd === odd) {
            rendererLogger.debug(`Item ${item.id} already plays on this buffer, keeping it loaded`);
            this.preloaded = { item, odd, mediaPath };
            return;
        }
        rendererLogger.debug(`Preloading ${item.id}`, { item: item, mediaPath: mediaPath });
        this.loadGen.set(Player.elementKey(odd, item.playable.type), this.getLoadGen(odd, item.playable.type) + 1);
        this.mainContainerElements[odd ? 'odd' : 'even'][item.playable.type].src = this.transformMediaPath(mediaPath);
        if (item.start_at) {
            try {
                // start_at is in milliseconds (server contract), currentTime in seconds.
                this.mainContainerElements[odd ? 'odd' : 'even'][item.playable.type].currentTime = (parseFloat(String(item.start_at)) || 0) / 1000;
            }
            catch (err) {
                rendererLogger.error('Failed to set start_at time during preload', { itemId: item.id, startAt: item.start_at });
            }
        }
        // Fade decision (spot → music): `item` is the successor being preloaded,
        // `currentlyPlaying` its predecessor. Music that resumes after an ad spot
        // is pre-set silent here and ramps up at play; every other audio item
        // (spots included) is pinned to full volume. Volume only — the start_at
        // seek above must stay untouched.
        if (item.playable.type === 'audio') {
            const audioElement = this.mainContainerElements[odd ? 'odd' : 'even'].audio;
            this.cancelRamp(audioElement);
            const fadeIn = !item.is_spot && this.currentlyPlaying?.item.is_spot === true;
            audioElement.volume = fadeIn ? 0 : 1;
            this.pendingFadeInItemId = fadeIn ? item.id : null;
        }
        else {
            this.pendingFadeInItemId = null;
        }
        this.preloaded = { item, odd, mediaPath };
    }
    handlePlayCommand(item, _odd, when) {
        // A lagging renderer (cold start) can handle the next item's preload before a
        // past-due play fires; keep our entry as long as nothing reloaded its buffer.
        const preloaded = this.preloaded?.item.id === item.id ? this.preloaded : null;
        const type = item.playable.type;
        const seq = ++this.playSeq;
        this.pendingPlays.set(seq, {
            item,
            when: when.getTime(),
            stopGen: this.stopGen,
            snapshot: preloaded ? { odd: preloaded.odd, type, gen: this.getLoadGen(preloaded.odd, type), mediaPath: preloaded.mediaPath } : null,
        });
        this.planAction(when, () => {
            const pending = this.pendingPlays.get(seq);
            this.pendingPlays.delete(seq);
            if (!pending) {
                rendererLogger.debug('Play dropped by the stop of its item, skipping', { requestedId: item.id });
                this.abandonDeferredSync(item.id);
                return;
            }
            if (pending.stopGen !== this.stopGen) {
                rendererLogger.debug('Play queued before a forced stop, skipping', { requestedId: item.id });
                this.abandonDeferredSync(item.id);
                return;
            }
            const wantedId = this.resolveNewestDuePlay(seq, item.id);
            if (wantedId !== item.id) {
                rendererLogger.debug('Play superseded by a newer one, skipping', { requestedId: item.id, newerId: wantedId });
                this.abandonDeferredSync(item.id);
                return;
            }
            const current = this.currentlyPlaying;
            const s = pending.snapshot;
            if (current?.item.id === item.id) {
                // Re-queued onto its own element (or nothing loaded for it): keep it playing. Re-pin the record so a forced stop
                // still pending for the old one no-ops. A copy loaded on the other buffer falls through and switches as before.
                const target = this.preloaded?.item.id === item.id ? this.preloaded : s && this.getLoadGen(s.odd, s.type) === s.gen ? s : null;
                if (!target || target.odd === current.odd) {
                    rendererLogger.debug('Play of the item already on screen, skipping', { requestedId: item.id });
                    const kept = { ...current };
                    this.currentlyPlaying = kept;
                    if (this.preloaded?.item.id === item.id)
                        this.preloaded = null;
                    this.runDeferredSync(kept, Promise.resolve());
                    return;
                }
            }
            let preloaded = this.preloaded;
            if (preloaded?.item.id !== item.id && s && this.getLoadGen(s.odd, s.type) === s.gen) {
                preloaded = { item, odd: s.odd, mediaPath: s.mediaPath };
            }
            if (!preloaded || preloaded.item.id !== item.id) {
                if (this.preloaded) {
                    rendererLogger.warn('Preloaded item no longer matches the requested item. Aborting play.', {
                        preloadedId: this.preloaded.item.id,
                        requestedId: item.id,
                    });
                }
                else {
                    rendererLogger.warn('No preloaded item found for the requested play item ID:' + item.id, {
                        preloadedId: null,
                        requestedId: item.id,
                    });
                }
                this.abandonDeferredSync(item.id);
                return;
            }
            rendererLogger.debug('Playing: ' + item.playable.name + ', planned at ' + when.toISOString());
            const playing = preloaded;
            this.currentlyPlaying = playing;
            if (this.preloaded?.item.id === item.id)
                this.preloaded = null;
            const started = this.play(type, playing.odd);
            if (type === 'video') {
                this.videoSync?.attach(this.mainContainerElements[playing.odd ? 'odd' : 'even'].video, {
                    itemId: item.id,
                    anchorMs: when.getTime(),
                    startAtMs: Number(item.start_at) || 0,
                });
            }
            // Fade execution at reveal: music following a spot ramps 0 → 1 (it was
            // pre-set silent at preload); anything else is guaranteed full volume.
            if (type === 'audio') {
                const audioElement = this.mainContainerElements[playing.odd ? 'odd' : 'even'].audio;
                if (this.pendingFadeInItemId === item.id) {
                    this.startRamp(audioElement, 1, this.fadeDuration);
                }
                else {
                    this.cancelRamp(audioElement);
                    audioElement.volume = 1;
                }
                if (this.pendingFadeInItemId === item.id)
                    this.pendingFadeInItemId = null;
            }
            this.runDeferredSync(playing, started);
        });
    }
    static elementKey(odd, type) {
        return (odd ? 'odd.' : 'even.') + type;
    }
    getLoadGen(odd, type) {
        return this.loadGen.get(Player.elementKey(odd, type)) ?? 0;
    }
    /** Records this play as due and returns the item of the newest due play (fired, or pending with its `when` reached). */
    resolveNewestDuePlay(seq, itemId) {
        if (seq > this.newestDuePlay.seq)
            this.newestDuePlay = { seq, itemId };
        let newest = this.newestDuePlay;
        const now = Date.now();
        for (const [pendingSeq, pending] of this.pendingPlays) {
            if (pendingSeq > newest.seq && pending.when <= now && pending.stopGen === this.stopGen)
                newest = { seq: pendingSeq, itemId: pending.item.id };
        }
        return newest.itemId;
    }
    hasPendingPlay(itemId) {
        for (const pending of this.pendingPlays.values()) {
            if (pending.item.id === itemId && pending.stopGen === this.stopGen)
                return true;
        }
        return false;
    }
    /** Forgets the item's plays yet to fire: their timers find no record and skip. */
    dropPendingPlays(itemId) {
        for (const [seq, pending] of this.pendingPlays) {
            if (pending.item.id === itemId)
                this.pendingPlays.delete(seq);
        }
    }
    /** An element still holding media for the preloaded successor or for a play yet to fire must survive stops and buffer switches. */
    isReserved(odd, type) {
        if (this.preloaded?.odd === odd && this.preloaded.item.playable.type === type)
            return true;
        const gen = this.getLoadGen(odd, type);
        for (const pending of this.pendingPlays.values()) {
            const s = pending.snapshot;
            if (pending.stopGen === this.stopGen && s && s.odd === odd && s.type === type && s.gen === gen)
                return true;
        }
        return false;
    }
    /** Drops a sync waiting on a play that never happened, unless another play for the item is still pending. */
    abandonDeferredSync(itemId) {
        if (this.hasPendingPlay(itemId) || !this.deferredSyncs.delete(itemId))
            return;
        rendererLogger.warn('Unable to sync playback, its play never started.', { requestedId: itemId });
        this.videoSync?.resume(itemId); // abandoned sync must not leave a later attach suspended
    }
    /**
     * Applies a stall-deferred late-join seek once play() has settled (pausing earlier would abort it), or after a
     * bounded wait when it never does; skipped if the record it was deferred for has left the screen by then.
     */
    runDeferredSync(playing, started) {
        const deferredSync = this.deferredSyncs.get(playing.item.id);
        if (!deferredSync)
            return;
        this.deferredSyncs.delete(playing.item.id);
        const bounded = new Promise(resolve => window.setTimeout(resolve, Player.DEFERRED_SYNC_WAIT_MS));
        Promise.race([started, bounded]).then(() => {
            if (this.currentlyPlaying === playing)
                deferredSync();
        });
    }
    /** Reveals and starts the element; the promise settles once its play() attempt (and retry) is over. */
    play(type, odd) {
        this.videoSync?.detach();
        this.showMainContainer();
        const elements = this.mainContainerElements[odd ? 'odd' : 'even'];
        this.mainContainerElements.idleContainer.classList.add('hidden');
        const idleInfo = this.mainContainerElements.idleContainer.querySelector('#idle-info');
        if (idleInfo) {
            idleInfo.classList.add('hidden');
        }
        this.hideBuffer(!odd, null, true); // the buffer just left is only paused: the next preload reassigns its src anyway
        this.hideBuffer(odd, type);
        const element = elements[type];
        element.classList.remove('hidden');
        let started = Promise.resolve();
        if (type === 'video') {
            const videoEl = element;
            videoEl.muted = !this.videoSoundEnabled;
            started = this.playWithAutoplayRetry(videoEl, 'Video');
        }
        if (type === 'audio') {
            const audioEl = element;
            audioEl.muted = false; // audio is always meant to be audible; mirror video's per-play muted reset. Volume untouched → fade-safe.
            started = this.playWithAutoplayRetry(audioEl, 'Audio');
            this.mainContainerElements.audioIcon.classList.remove('hidden');
        }
        if (type === 'web') {
            // The iframe has been loaded (and a self-timed render shell self-advancing)
            // since preload; re-anchor its slide timeline to "now" so it shows from the
            // start at reveal. Only signal OUR render shells (server stamps web_url with
            // bhx_ctl — see EventListPayloadService); third-party sites are left alone.
            // Cross-origin postMessage is the only channel that reaches a framed shell.
            // Mirrors ElectronWebPlayer's executeJavaScript signal.
            const current = this.currentlyPlaying;
            if (current && (current.item.playable.web_url ?? '').includes('bhx_ctl=1')) {
                const iframe = element;
                try {
                    iframe.contentWindow?.postMessage({ source: 'bohemica-signage', v: 1, type: 'bhx:start', itemId: current.item.id, t0: Date.now() }, '*');
                }
                catch (err) {
                    rendererLogger.warn('Web start signal failed', { err: String(err) });
                }
            }
        }
        return started;
    }
    /** Hides a buffer's elements (all but `except`); the unreserved ones are unloaded, or only paused when `keepLoaded`. */
    hideBuffer(odd, except = null, keepLoaded = false) {
        const elements = this.mainContainerElements[odd ? 'odd' : 'even'];
        for (const key in elements) {
            if (key === except)
                continue;
            const element = elements[key];
            element.classList.add('hidden');
            if (!this.isReserved(odd, key)) {
                if (keepLoaded)
                    this.stopMediaElement(element);
                else
                    this.clearMediaElement(element);
            }
            if (key === 'audio') {
                this.mainContainerElements.audioIcon.classList.add('hidden');
            }
        }
    }
    /**
     * Plays a media element, retrying once muted on a NotAllowedError autoplay
     * block. Browsers block audible autoplay until the user interacts with the
     * page; signage runs on a fresh, never-touched tab, so an audible item would
     * otherwise never start. Retrying muted keeps the schedule advancing — a
     * muted audio spot is silent by definition, but the item plays out instead of
     * stalling and the notice makes the root cause visible. `label` (Audio /
     * Video / …) distinguishes the source in the logs. Touches `muted` only —
     * never volume — so the fade ramps set by the caller are left intact.
     * The returned promise settles once the attempt (and retry) is over.
     */
    playWithAutoplayRetry(element, label) {
        return element.play().catch(err => {
            if (err && err.name === 'NotAllowedError' && !element.muted) {
                rendererLogger.notice(`${label} autoplay blocked; retrying muted`, { err: String(err) });
                element.muted = true;
                return element.play().catch(err2 => rendererLogger.warn(`${label} play failed (muted retry)`, { err: String(err2) }));
            }
            rendererLogger.warn(`${label} play failed`, { err: String(err) });
        });
    }
    handleStopCommand(item, when, force = false) {
        if (force)
            this.stopGen++;
        this.videoSync?.setItemEnd(item.id, when.getTime());
        // Fade decision (music → spot): plan the ramp so it COMPLETES at the stop
        // `when` (i.e. starts at when - fadeDuration). Whether it actually runs is
        // decided when the timer fires — by then the double buffer knows both
        // neighbors (currentlyPlaying = the item being stopped, preloaded = its
        // successor). Forced stops cut hard, and the stop itself is never delayed:
        // a late/short window only clamps the ramp.
        if (!force && this.fadeDuration > 0) {
            this.planAction(new Date(when.getTime() - this.fadeDuration), () => {
                const current = this.currentlyPlaying;
                if (!current || current.item.id !== item.id) {
                    return; // superseded or not playing yet — no fade
                }
                if (current.item.playable.type !== 'audio' || current.item.is_spot) {
                    return; // only music fades; spots and video are never ramped
                }
                if (this.preloaded?.item.is_spot !== true) {
                    return; // successor unknown or not a spot — hard cut (music → music)
                }
                const remainingMs = when.getTime() - Date.now();
                if (remainingMs <= 0) {
                    return; // window already gone — hard cut rather than delaying the stop
                }
                const audioElement = this.mainContainerElements[current.odd ? 'odd' : 'even'].audio;
                this.startRamp(audioElement, 0, Math.min(this.fadeDuration, remainingMs));
            });
        }
        // A forced stop skips the id check, so pin it to what is on screen now: the
        // replacement item's play (2 ms poll) fires before this stop (10 ms poll).
        const forcedTarget = force ? this.currentlyPlaying : null;
        this.planAction(when, () => {
            const current = this.currentlyPlaying;
            if (!current || (force ? current !== forcedTarget : current.item.id !== item.id)) {
                return;
            }
            rendererLogger.debug('Stopping playback for ID:' + item.id + ', planned at ' + when.toISOString());
            this.videoSync?.detach();
            // Its slot is over: a re-queued play of the item must neither keep its element loaded nor bring it back.
            if (!force)
                this.dropPendingPlays(current.item.id);
            if (this.preloaded?.item.id === current.item.id)
                this.preloaded = null;
            this.hideBuffer(current.odd);
            this.currentlyPlaying = null;
        }, 10);
    }
    handleIdleCommand(isSomethingPlanned, nextItemAt, isExiting = false) {
        this.clearAllTimers();
        this.pendingPlays.clear();
        this.deferredSyncs.clear();
        this.videoSync?.reset();
        this.currentlyPlaying = null;
        this.preloaded = null;
        this.pendingFadeInItemId = null;
        this.mainContainerElements.idleContainer.classList.remove('hidden');
        this.hideBuffer(true);
        this.hideBuffer(false);
        const idleInfo = this.mainContainerElements.idleContainer.querySelector('#idle-info');
        if (idleInfo) {
            if (isExiting) {
                idleInfo.innerHTML = tHtml('exiting');
                idleInfo.classList.remove('hidden');
            }
            else if (isSomethingPlanned && nextItemAt) {
                idleInfo.innerHTML = tHtml('next_item_at', { date: nextItemAt.toLocaleString() });
                idleInfo.classList.remove('hidden');
            }
            else if (!isSomethingPlanned) {
                idleInfo.innerHTML = tHtml('nothing_planned');
                idleInfo.classList.remove('hidden');
            }
            else {
                idleInfo.classList.add('hidden');
            }
        }
    }
    handleSyncCommand(item, odd, when, seekTo) {
        if (item.playable.type === 'video')
            this.videoSync?.expectSync(item.id);
        const applySync = () => {
            if (item.playable.type !== 'video' && item.playable.type !== 'audio') {
                rendererLogger.warn('Unable to sync playback, unsupported media type for syncing: ' + item.playable.type);
                return;
            }
            const elements = this.mainContainerElements[odd ? 'odd' : 'even'];
            const mediaElement = elements[item.playable.type];
            mediaElement.pause();
            mediaElement.currentTime = seekTo / 1000;
            this.planAction(when, () => {
                rendererLogger.debug('Syncing playback for ID:' + item.id + ' to ' + seekTo + 'ms, planned at ' + when.toISOString());
                // Same autoplay-block retry as the initial play paths; the sync-resume
                // play() is shared by video and audio, so label by the item's type.
                const syncLabel = item.playable.type === 'video' ? 'Video (sync)' : 'Audio (sync)';
                try {
                    this.playWithAutoplayRetry(mediaElement, syncLabel);
                }
                catch (err) {
                    rendererLogger.error('Failed to resume playback after sync', { itemId: item.id, seekTo });
                }
                this.videoSync?.resume(item.id);
            });
        };
        const syncTimeout = window.setTimeout(() => {
            this.activeTimers.delete(syncTimeout);
            if (!this.currentlyPlaying || this.currentlyPlaying.item.id !== item.id) {
                if (this.hasPendingPlay(item.id)) {
                    // Renderer stall: the play is due but its poll has not fired yet — seek right after it does.
                    this.deferredSyncs.set(item.id, applySync);
                    return;
                }
                rendererLogger.warn('Unable to sync playback, no currently playing item or ID does not match.', {
                    currentPlayingId: this.currentlyPlaying ? this.currentlyPlaying.item.id : null,
                    requestedId: item.id,
                });
                this.videoSync?.resume(item.id); // abandoned sync must not leave a later attach suspended
                return;
            }
            applySync();
        }, 1500);
        this.activeTimers.add(syncTimeout);
    }
    clearMediaElement(element) {
        if (element instanceof HTMLVideoElement || element instanceof HTMLAudioElement) {
            this.cancelRamp(element);
            element.pause();
            element.removeAttribute('src');
            element.srcObject = null;
            // Restore AFTER pause: a reused buffer element must never stay quiet
            // from an earlier fade (double buffers recycle these elements).
            element.volume = 1;
            element.playbackRate = 1;
            // Previously we called element.load() here to force release of decoded
            // frame buffers (OOM on RPi 4GB). On x86 with hardware-accelerated video
            // decode + a single 4K@50Hz HDMI sink (LG over Gemini Lake), the back-
            // to-back load() at every transition tears down a hardware overlay
            // plane mid-frame and can drive a brief HDMI re-train visible to
            // downstream sinks. Pause + src removal alone allow Chromium's media
            // pipeline to release the decoded buffers on its own schedule without
            // the synchronous KMS atomic commit. If RPi memory pressure becomes a
            // problem again we should gate load() on a platform flag.
        }
        else if (element instanceof HTMLIFrameElement) {
            // Removing src doesn't unload an iframe's document; navigate it away so
            // a stopped WEB playable stops (audio, timers).
            if (element.getAttribute('src'))
                element.src = 'about:blank';
        }
        else {
            element.removeAttribute('src');
        }
    }
    stopMediaElement(element) {
        if (element instanceof HTMLVideoElement || element instanceof HTMLAudioElement) {
            this.cancelRamp(element);
            if (!element.paused) {
                element.pause();
                element.currentTime = 0;
            }
            // Restore AFTER pause so a mid-fade element never blips back audible;
            // also covers a paused element left silent by an aborted fade-in.
            element.volume = 1;
            element.playbackRate = 1;
        }
    }
    /** Cancels a running volume ramp on a media element, if any. */
    cancelRamp(element) {
        if (element instanceof HTMLVideoElement || element instanceof HTMLAudioElement) {
            this.activeRamps.get(element)?.cancel();
            this.activeRamps.delete(element);
        }
    }
    startRamp(element, target, durationMs) {
        this.cancelRamp(element);
        this.activeRamps.set(element, rampVolume(element, target, durationMs));
    }
    clearAllTimers() {
        for (const timer of this.activeTimers) {
            clearTimeout(timer);
            clearInterval(timer);
        }
        this.activeTimers.clear();
    }
    planAction(when, callback, intervalDelay = 2) {
        const scheduledForFuture = when.getTime() > Date.now();
        const isMoreThanFiveSecondsAway = when.getTime() - Date.now() > 5000;
        const timeout = window.setTimeout(() => {
            const interval = window.setInterval(() => {
                if (Date.now() >= when.getTime()) {
                    clearInterval(interval);
                    this.activeTimers.delete(interval);
                    this.activeTimers.delete(timeout);
                    const delayMs = Date.now() - when.getTime();
                    if (scheduledForFuture && delayMs > Player.PLAN_ACTION_DELAY_WARN_MS) {
                        rendererLogger.warn('planAction callback fired late — likely renderer event-loop block', {
                            delayMs,
                            plannedAt: when.toISOString(),
                            firedAt: new Date().toISOString(),
                        });
                    }
                    callback();
                }
            }, intervalDelay);
            this.activeTimers.add(interval);
        }, isMoreThanFiveSecondsAway ? when.getTime() - Date.now() - 5000 : 0);
        this.activeTimers.add(timeout);
        return timeout;
    }
}
Player.PLAN_ACTION_DELAY_WARN_MS = 500;
Player.DEFERRED_SYNC_WAIT_MS = 2000;
//# sourceMappingURL=player.js.map